/**
 * Performance document v1 — single file: rig, program map, presets (slots).
 * @see docs/chats.md
 */

export const PERFORMANCE_VERSION = 1;

/**
 * Shared environment controls (lighting, backdrop, bloom, orbit limits).
 * Scenes read only the keys they understand; others are ignored.
 * Orbit limits use `orbitMinDistance` / `orbitMaxDistance` everywhere (marching uses them as world units;
 * CA hi-fi maps the same values into its smaller scale — see CAHiFiCoreScene).
 */
export const GLOBAL_ENV_PARAM_SPECS = {
  dirLightIntensity: { min: 0, max: 12, label: 'Dir. light intensity' },
  dirLightColor: { type: 'color', label: 'Dir. light color' },
  dirLightPosX: { min: -3, max: 3, label: 'Dir. light X' },
  dirLightPosY: { min: -3, max: 3, label: 'Dir. light Y' },
  dirLightPosZ: { min: -3, max: 3, label: 'Dir. light Z' },
  pointLightIntensity: { min: 0, max: 12, label: 'Point light intensity' },
  pointLightColor: { type: 'color', label: 'Point light color' },
  pointLightPosX: { min: -800, max: 800, label: 'Point light X' },
  pointLightPosY: { min: -800, max: 800, label: 'Point light Y' },
  pointLightPosZ: { min: -800, max: 800, label: 'Point light Z' },
  ambientIntensity: { min: 0, max: 12, label: 'Ambient intensity' },
  ambientColor: { type: 'color', label: 'Ambient color' },
  backgroundColor: { type: 'color', label: 'Background' },
  orbitMinDistance: { min: 100, max: 4000, label: 'Orbit min distance' },
  orbitMaxDistance: { min: 500, max: 20000, label: 'Orbit max distance' },
  cameraFov: { min: 20, max: 90, label: 'Camera FOV' },
  bloomStrength: { min: 0, max: 2, label: 'Bloom strength' },
  bloomRadius: { min: 0, max: 1, label: 'Bloom radius' },
  bloomThreshold: { min: 0, max: 1, label: 'Bloom threshold' },
};

export const GLOBAL_ENV_DEFAULT_PARAMS = {
  dirLightIntensity: 3,
  dirLightColor: '#ffffff',
  dirLightPosX: 0.5,
  dirLightPosY: 0.5,
  dirLightPosZ: 1,
  pointLightIntensity: 3,
  pointLightColor: '#ff7c00',
  pointLightPosX: 0,
  pointLightPosY: 0,
  pointLightPosZ: 100,
  ambientIntensity: 3,
  ambientColor: '#323232',
  backgroundColor: '#050505',
  orbitMinDistance: 500,
  orbitMaxDistance: 5000,
  cameraFov: 45,
  bloomStrength: 0.9,
  bloomRadius: 0.4,
  bloomThreshold: 0.2,
};

/** @type {ReadonlySet<string>} */
export const GLOBAL_ENV_KEYS = new Set(Object.keys(GLOBAL_ENV_DEFAULT_PARAMS));

/**
 * Bloom / threshold params are read by `ThreeRenderer` after every scene — always in effect.
 * @type {ReadonlySet<string>}
 */
export const GLOBAL_ENV_POST_KEYS = new Set(['bloomStrength', 'bloomRadius', 'bloomThreshold']);

/**
 * Which shared environment keys each scene actually reads (excluding post-process bloom keys).
 * Keep in sync with scene `applyParams` / frame logic.
 * @type {Record<string, ReadonlySet<string>>}
 */
export const GLOBAL_ENV_SCENE_KEYS_BY_SCENE_ID = {
  marching_cubes: new Set([
    'dirLightIntensity',
    'dirLightColor',
    'dirLightPosX',
    'dirLightPosY',
    'dirLightPosZ',
    'pointLightIntensity',
    'pointLightColor',
    'pointLightPosX',
    'pointLightPosY',
    'pointLightPosZ',
    'ambientIntensity',
    'ambientColor',
    'backgroundColor',
    'orbitMinDistance',
    'orbitMaxDistance',
    'cameraFov',
  ]),
  ca_hifi_core: new Set([
    'dirLightIntensity',
    'dirLightColor',
    'dirLightPosX',
    'dirLightPosY',
    'dirLightPosZ',
    'pointLightIntensity',
    'pointLightColor',
    'pointLightPosX',
    'pointLightPosY',
    'pointLightPosZ',
    'ambientIntensity',
    'ambientColor',
    'backgroundColor',
    'orbitMinDistance',
    'orbitMaxDistance',
    'cameraFov',
  ]),
  pulse: new Set(),
  wave: new Set(),
  iridescent_wave: new Set(),
};

/**
 * @param {string | null | undefined} sceneId
 * @param {string} envKey
 * @returns {{ kind: 'post' | 'scene' | 'unused', hint?: string }}
 */
export function getGlobalEnvKeyUsage(sceneId, envKey) {
  if (GLOBAL_ENV_POST_KEYS.has(envKey)) {
    return {
      kind: 'post',
      hint: 'Runs on the final image. On dark scenes the change can look subtle until something is bright enough to bloom.',
    };
  }
  const sid = String(sceneId || '');
  const used = GLOBAL_ENV_SCENE_KEYS_BY_SCENE_ID[sid];
  if (used?.has(envKey)) return { kind: 'scene' };
  return {
    kind: 'unused',
    hint: 'This scene does not read this key. Marching cubes and CA hi-fi use the same lighting / backdrop / orbit / FOV set.',
  };
}

/**
 * @param {string | null | undefined} sceneId
 * @returns {{ scene: number, post: number, unused: number }}
 */
export function summarizeGlobalEnvUsageForScene(sceneId) {
  const out = { scene: 0, post: 0, unused: 0 };
  for (const key of GLOBAL_ENV_KEYS) {
    out[getGlobalEnvKeyUsage(sceneId, key).kind]++;
  }
  return out;
}

/** MIDI binding targets for global env params (manual + live). */
export const GLOBAL_ENV_BINDING_TARGETS = [
  { key: 'cameraFov', label: 'Camera FOV', min: 0, max: 1 },
  { key: 'dirLightIntensity', label: 'Dir. light brightness', min: 0, max: 1 },
  { key: 'pointLightIntensity', label: 'Point light brightness', min: 0, max: 1 },
  { key: 'ambientIntensity', label: 'Ambient brightness', min: 0, max: 1 },
  { key: 'pointLightPosZ', label: 'Point light depth', min: 0, max: 1 },
];

const HEX_COLOR_RE = /^#[0-9a-fA-F]{3,8}$/;
const FUTURE_CONTROL_SOURCES = new Set(['gui', 'uniform', 'prop', 'imported', 'discovered']);
const FUTURE_CONTROL_CONFIDENCE = new Set(['high', 'medium', 'low']);

function hasOwn(obj, key) {
  return !!obj && Object.prototype.hasOwnProperty.call(obj, key);
}

/**
 * Where the merged value comes from (preset env overlay wins over performance globalEnv).
 * @param {Partial<PerformanceV1> | null | undefined} performance
 * @param {Partial<PresetV1> | null | undefined} preset
 * @param {string} envKey
 * @returns {'default' | 'shared' | 'preset'}
 */
export function getGlobalEnvValueSource(performance, preset, envKey) {
  const envOverrides =
    preset?.envOverrides && typeof preset.envOverrides === 'object' ? preset.envOverrides : {};
  if (hasOwn(envOverrides, envKey)) return 'preset';
  const globalEnv =
    performance?.globalEnv && typeof performance.globalEnv === 'object' ? performance.globalEnv : {};
  if (hasOwn(globalEnv, envKey)) return 'shared';
  return 'default';
}

function formatControlLabel(key) {
  return String(key)
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^\w/, (c) => c.toUpperCase());
}

function isFiniteNumber(value) {
  return typeof value === 'number' && Number.isFinite(value);
}

function inferControlKind(defaultValue, paramSpec, bindingTarget) {
  if (paramSpec?.type === 'hue' || paramSpec?.type === 'color') return 'color';
  if (Array.isArray(paramSpec?.options) && paramSpec.options.length) return 'enum';
  if (typeof defaultValue === 'boolean') return 'boolean';
  if (isFiniteNumber(defaultValue)) return 'number';
  if (typeof defaultValue === 'string') {
    return HEX_COLOR_RE.test(defaultValue.trim()) ? 'color' : 'enum';
  }
  if (isFiniteNumber(bindingTarget?.min) || isFiniteNumber(bindingTarget?.max)) return 'number';
  if (paramSpec?.type === 'number') return 'number';
  return 'text';
}

function buildControlUi(kind, defaultValue, paramSpec, bindingTarget) {
  if (kind === 'number') {
    const min =
      isFiniteNumber(paramSpec?.min)
        ? paramSpec.min
        : isFiniteNumber(bindingTarget?.min)
          ? bindingTarget.min
          : 0;
    const max =
      isFiniteNumber(paramSpec?.max)
        ? paramSpec.max
        : isFiniteNumber(bindingTarget?.max)
          ? bindingTarget.max
          : isFiniteNumber(defaultValue) && defaultValue >= 0 && defaultValue <= 1
            ? 1
            : Math.max(1, Math.abs(Number(defaultValue) || 0) * 4, 128);
    const step = isFiniteNumber(paramSpec?.step)
      ? paramSpec.step
      : Number.isInteger(defaultValue)
        ? 1
        : undefined;
    return step === undefined ? { min, max } : { min, max, step };
  }
  if (kind === 'enum' && Array.isArray(paramSpec?.options) && paramSpec.options.length) {
    return { options: [...paramSpec.options] };
  }
  return undefined;
}

function normalizeFutureControlKind(control) {
  const raw = String(control?.kind || control?.type || '').trim().toLowerCase();
  if (
    raw === 'number' ||
    raw === 'float' ||
    raw === 'int' ||
    raw === 'integer' ||
    raw === 'range'
  ) {
    return 'number';
  }
  if (raw === 'boolean' || raw === 'bool') return 'boolean';
  if (raw === 'enum' || raw === 'select' || raw === 'choice') return 'enum';
  if (raw === 'color' || raw === 'hue') return 'color';
  return 'text';
}

function normalizeFutureControlUi(control, kind) {
  const ui = control?.ui && typeof control.ui === 'object' ? control.ui : {};
  if (kind === 'number') {
    const out = {};
    if (isFiniteNumber(ui.min)) out.min = ui.min;
    if (isFiniteNumber(ui.max)) out.max = ui.max;
    if (isFiniteNumber(ui.step)) out.step = ui.step;
    return Object.keys(out).length ? out : undefined;
  }
  const options = Array.isArray(control?.options)
    ? control.options
    : Array.isArray(ui.options)
      ? ui.options
      : null;
  if (kind === 'enum' && options?.length) {
    return { options: options.map((option) => String(option)) };
  }
  return undefined;
}

function normalizeFutureControlSource(source, fallback) {
  const raw = String(source || fallback || '').trim().toLowerCase();
  return FUTURE_CONTROL_SOURCES.has(raw) ? raw : fallback;
}

function normalizeFutureControlConfidence(confidence) {
  const raw = String(confidence || '').trim().toLowerCase();
  return FUTURE_CONTROL_CONFIDENCE.has(raw) ? raw : undefined;
}

function normalizeFutureControlSeed(control, index, fallbackSource) {
  if (!control || typeof control !== 'object') return null;
  const rawKey = control.key ?? control.id ?? control.label ?? `${fallbackSource}_${index + 1}`;
  const key = String(rawKey || '').trim();
  if (!key) return null;
  const kind = normalizeFutureControlKind(control);
  const defaultValue =
    control.defaultValue !== undefined
      ? control.defaultValue
      : control.value !== undefined
        ? control.value
        : undefined;
  return {
    key,
    label: control.label ? String(control.label) : formatControlLabel(key),
    kind,
    defaultValue,
    hasManualValue: defaultValue !== undefined,
    bindable: control.bindable === true,
    bindingDefault: control.bindingDefault ? String(control.bindingDefault) : '',
    ui: normalizeFutureControlUi(control, kind),
    legacy: {},
    origin: 'discovered',
    source: normalizeFutureControlSource(control.source, fallbackSource),
    confidence: normalizeFutureControlConfidence(control.confidence),
  };
}

function getFutureControlSeeds(preset) {
  const discovered = Array.isArray(preset?.discoveredControls) ? preset.discoveredControls : [];
  const imported = Array.isArray(preset?.importedControls) ? preset.importedControls : [];
  return [
    ...discovered
      .map((control, index) => normalizeFutureControlSeed(control, index, 'discovered'))
      .filter(Boolean),
    ...imported
      .map((control, index) => normalizeFutureControlSeed(control, index, 'imported'))
      .filter(Boolean),
  ];
}

function updateFutureControlCollection(list, controlKey, patch) {
  if (!Array.isArray(list) || !list.length) return list;
  return list.map((control, index) => {
    const normalized = normalizeFutureControlSeed(control, index, control?.source || 'discovered');
    if (!normalized || normalized.key !== controlKey) return control;
    return { ...control, ...patch };
  });
}

/**
 * @typedef {Object} RigSignalDef
 * @property {'midi_note'|'midi_cc'|'audio_rms'|'audio_band'|'engine'} type
 * @property {number} [channel] 1–16 for MIDI types
 * @property {number} [note] 0–127 for midi_note (optional = any note on channel)
 * @property {number} [cc] 0–127 for midi_cc
 * @property {'bass'|'lowMid'|'mid'|'highMid'|'treble'} [band] for audio_band
 * @property {number} [release] ms decay; 0 = hold (CC) or instant off for notes
 */

/**
 * @typedef {Object} PresetV1
 * @property {string} scene Scene implementation id (registry)
 * @property {number} programNumber 0–127
 * @property {string} [transition] 'cut' | 'fade' | 'flash'
 * @property {Record<string, string>} bindings local param → rig signal name
 * @property {Record<string, unknown>} [params]
 * @property {string} [displayName] Human-readable label in the UI (preset id key stays stable)
 * @property {Array<{
 *   key?: string,
 *   id?: string,
 *   label?: string,
 *   kind?: string,
 *   type?: string,
 *   defaultValue?: unknown,
 *   value?: unknown,
 *   bindable?: boolean,
 *   bindingDefault?: string,
 *   source?: string,
 *   confidence?: 'high'|'medium'|'low',
 *   options?: string[],
 *   ui?: { min?: number, max?: number, step?: number, options?: string[] }
 * }>} [discoveredControls]
 * @property {PresetV1['discoveredControls']} [importedControls]
 * @property {Record<string, unknown>} [envOverrides] Per-preset overlay for global environment keys
 */

/**
 * @typedef {Object} PerformanceV1
 * @property {string} [name]
 * @property {number} [version]
 * @property {object} [rig]
 * @property {Record<string, string>} [programMap]
 * @property {Record<string, PresetV1>} [presets]
 * @property {Record<string, unknown>} [globalEnv] Shared environment defaults for all presets
 */

/**
 * @returns {import('./schema.js').PerformanceV1}
 */
export function createDefaultPerformance() {
  return {
    name: 'Untitled performance',
    version: PERFORMANCE_VERSION,
    globalEnv: {},
    rig: {
      signals: {
        amplitude: { type: 'audio_rms', release: 0 },
        bass_energy: { type: 'audio_band', band: 'bass', release: 0 },
        mid_energy: { type: 'audio_band', band: 'mid', release: 0 },
        bpm_pulse: { type: 'engine', release: 0 },
      },
    },
    programMap: {
      0: 'marching',
      1: 'main',
    },
    presets: {
      main: {
        displayName: 'Main',
        scene: 'iridescent_wave',
        programNumber: 1,
        transition: 'cut',
        bindings: {},
        params: {},
      },
      marching: {
        displayName: 'Marching cubes',
        scene: 'marching_cubes',
        programNumber: 0,
        transition: 'cut',
        bindings: {},
        params: {},
      },
    },
  };
}

/**
 * @param {unknown} data
 * @returns {data is { version?: number, presets?: object, programMap?: object, rig?: object }}
 */
export function looksLikePerformance(data) {
  return data != null && typeof data === 'object';
}

/**
 * Declarative metadata object for deriving Environment tab controls.
 * @returns {{
 *   defaultParams: Record<string, unknown>,
 *   paramSpecs: Record<string, unknown>,
 *   defaultBindings: Record<string, string>,
 *   bindingTargets: typeof GLOBAL_ENV_BINDING_TARGETS,
 * }}
 */
export function getGlobalEnvSceneMeta() {
  return {
    defaultParams: { ...GLOBAL_ENV_DEFAULT_PARAMS },
    paramSpecs: { ...GLOBAL_ENV_PARAM_SPECS },
    defaultBindings: {},
    bindingTargets: [...GLOBAL_ENV_BINDING_TARGETS],
  };
}

/**
 * @param {Partial<PerformanceV1> | null | undefined} performance
 * @param {Partial<PresetV1> | null | undefined} preset
 * @returns {Record<string, unknown>}
 */
export function getMergedEffectiveEnv(performance, preset) {
  const globalEnv = performance?.globalEnv && typeof performance.globalEnv === 'object' ? performance.globalEnv : {};
  const envOverrides =
    preset?.envOverrides && typeof preset.envOverrides === 'object' ? preset.envOverrides : {};
  return {
    ...GLOBAL_ENV_DEFAULT_PARAMS,
    ...globalEnv,
    ...envOverrides,
  };
}

/**
 * @param {{
 *   defaultParams?: Record<string, unknown>,
 *   paramSpecs?: Record<string, any>,
 *   bindingTargets?: Array<{ key: string, label?: string, min?: number, max?: number }>,
 *   defaultBindings?: Record<string, string>,
 * }} sceneMeta
 * @param {Partial<PresetV1>} [preset]
 * @returns {SceneControlDef[]}
 */
export function deriveSceneControlDefsForSceneTab(sceneMeta, preset) {
  return deriveSceneControlDefs(sceneMeta, preset).filter((c) => !GLOBAL_ENV_KEYS.has(c.key));
}

/**
 * @param {Partial<PerformanceV1> | null | undefined} performance
 * @param {Partial<PresetV1> | null | undefined} preset
 * @returns {SceneControlDef[]}
 */
export function deriveGlobalEnvControlDefs(performance, preset) {
  const mergedEnv = getMergedEffectiveEnv(performance, preset);
  const meta = getGlobalEnvSceneMeta();
  const bindings = preset?.bindings && typeof preset.bindings === 'object' ? preset.bindings : {};
  /** @type {Record<string, string>} */
  const envBindings = {};
  for (const key of Object.keys(bindings)) {
    if (GLOBAL_ENV_KEYS.has(key)) envBindings[key] = String(bindings[key] || '');
  }
  return deriveSceneControlDefs(meta, {
    params: mergedEnv,
    bindings: envBindings,
  });
}

/**
 * @param {{
 *   defaultBindings?: Record<string, string>,
 *   defaultParams?: Record<string, unknown>,
 * }} sceneMeta
 * @param {Partial<PerformanceV1> | null | undefined} performance
 * @param {Partial<PresetV1> | null | undefined} preset
 * @returns {Partial<PresetV1> & { params: Record<string, unknown>, bindings: Record<string, string> }}
 */
export function resolveRuntimeParams(sceneMeta, performance, preset) {
  const legacy = translateUnifiedPresetToLegacy(sceneMeta, preset);
  const effectiveEnv = getMergedEffectiveEnv(performance, preset);
  const params = {
    ...(sceneMeta?.defaultParams || {}),
    ...(preset?.params || {}),
    ...effectiveEnv,
  };
  return { ...legacy, params };
}

/**
 * Merge preset bindings/params with scene defaults.
 * @param {{ defaultBindings?: Record<string, string>, defaultParams?: Record<string, unknown> }} sceneMeta
 * @param {Partial<PresetV1>} preset
 */
export function mergePresetWithSceneDefaults(sceneMeta, preset) {
  return translateUnifiedPresetToLegacy(sceneMeta, preset);
}

/**
 * Compatibility control definition derived from split legacy scene metadata.
 * This does not invent links between unrelated param keys and binding keys; it
 * simply normalizes what the scene currently exposes so the UI can evolve first.
 *
 * @typedef {Object} SceneControlDef
 * @property {string} key Stable UI key for the control row
 * @property {string} label Human-friendly control label
 * @property {'number'|'boolean'|'enum'|'color'|'text'} kind
 * @property {unknown} [defaultValue] Manual baseline default when legacy params expose one
 * @property {boolean} hasManualValue True when backed by legacy `params`
 * @property {boolean} bindable True when backed by legacy `bindingTargets` / `bindings`
 * @property {string} bindingDefault Default or current legacy binding source name
 * @property {{ min?: number, max?: number, step?: number, options?: string[] } | undefined} ui
 * @property {{ paramKey?: string, bindingKey?: string }} legacy
 * @property {'scene'|'preset'|'discovered'} origin
 * @property {'gui'|'uniform'|'prop'|'imported'|'discovered'|undefined} [source]
 * @property {'high'|'medium'|'low'|undefined} [confidence]
 */

/**
 * Derive ordered unified control definitions from current split scene metadata.
 * Preset-only keys are preserved so the future UI does not silently drop legacy
 * saved values that no longer exist in the current scene metadata.
 *
 * @param {{
 *   defaultParams?: Record<string, unknown>,
 *   paramSpecs?: Record<string, any>,
 *   bindingTargets?: Array<{ key: string, label?: string, min?: number, max?: number }>,
 *   defaultBindings?: Record<string, string>,
 * }} sceneMeta
 * @param {Partial<PresetV1>} [preset]
 * @returns {SceneControlDef[]}
 */
export function deriveSceneControlDefs(sceneMeta, preset) {
  const defaultParams = sceneMeta?.defaultParams || {};
  const paramSpecs = sceneMeta?.paramSpecs || {};
  const bindingTargets = Array.isArray(sceneMeta?.bindingTargets) ? sceneMeta.bindingTargets : [];
  const defaultBindings = sceneMeta?.defaultBindings || {};
  const presetParams = preset?.params || {};
  const presetBindings = preset?.bindings || {};
  const futureControls = getFutureControlSeeds(preset);

  /** @type {Map<string, { key: string, label?: string, min?: number, max?: number }>} */
  const bindingTargetByKey = new Map();
  for (const target of bindingTargets) {
    if (!target?.key) continue;
    bindingTargetByKey.set(target.key, target);
  }

  const orderedKeys = [];
  const seen = new Set();
  const pushKey = (key) => {
    if (!key || seen.has(key)) return;
    seen.add(key);
    orderedKeys.push(key);
  };

  Object.keys(defaultParams).forEach(pushKey);
  Object.keys(paramSpecs).forEach(pushKey);
  bindingTargets.forEach((target) => pushKey(target?.key));
  Object.keys(defaultBindings).forEach(pushKey);
  Object.keys(presetParams).forEach(pushKey);
  Object.keys(presetBindings).forEach(pushKey);
  futureControls.forEach((control) => pushKey(control.key));

  return orderedKeys.map((key) => {
    const futureControl = futureControls.find((control) => control.key === key);
    if (futureControl) return futureControl;

    const paramSpec = paramSpecs[key];
    const bindingTarget = bindingTargetByKey.get(key);
    const hasManualValue = hasOwn(defaultParams, key) || hasOwn(presetParams, key);
    const bindable =
      !!bindingTarget || hasOwn(defaultBindings, key) || hasOwn(presetBindings, key);
    const defaultValue = hasOwn(presetParams, key)
      ? presetParams[key]
      : hasOwn(defaultParams, key)
        ? defaultParams[key]
        : undefined;
    const bindingDefault = hasOwn(presetBindings, key)
      ? String(presetBindings[key] || '')
      : hasOwn(defaultBindings, key)
        ? String(defaultBindings[key] || '')
        : '';
    const kind = inferControlKind(defaultValue, paramSpec, bindingTarget);

    return {
      key,
      label: paramSpec?.label || bindingTarget?.label || formatControlLabel(key),
      kind,
      defaultValue,
      hasManualValue,
      bindable,
      bindingDefault,
      ui: buildControlUi(kind, defaultValue, paramSpec, bindingTarget),
      legacy: {
        ...(hasManualValue ? { paramKey: key } : {}),
        ...(bindable ? { bindingKey: key } : {}),
      },
      origin:
        hasOwn(defaultParams, key) ||
        hasOwn(paramSpecs, key) ||
        bindingTargetByKey.has(key) ||
        hasOwn(defaultBindings, key)
          ? 'scene'
          : 'preset',
    };
  });
}

/**
 * Translate the current unified control representation back into the legacy
 * runtime shape that scenes already consume (`params` + `bindings`).
 *
 * @param {{
 *   defaultBindings?: Record<string, string>,
 *   defaultParams?: Record<string, unknown>,
 * }} sceneMeta
 * @param {Partial<PresetV1>} preset
 * @returns {Partial<PresetV1> & { params: Record<string, unknown>, bindings: Record<string, string> }}
 */
export function translateUnifiedPresetToLegacy(sceneMeta, preset) {
  const bindings = {
    ...(sceneMeta?.defaultBindings || {}),
    ...(preset?.bindings || {}),
  };
  const params = {
    ...(sceneMeta?.defaultParams || {}),
    ...(preset?.params || {}),
  };
  return { ...preset, bindings, params };
}

/**
 * Apply a unified manual-value edit and return a legacy-compatible preset.
 * Future discovered/imported controls are stored back into their seed arrays
 * until they gain a real runtime adapter.
 *
 * @param {{
 *   defaultBindings?: Record<string, string>,
 *   defaultParams?: Record<string, unknown>,
 *   paramSpecs?: Record<string, any>,
 *   bindingTargets?: Array<{ key: string, label?: string, min?: number, max?: number }>,
 * }} sceneMeta
 * @param {Partial<PresetV1>} preset
 * @param {string} controlKey
 * @param {unknown} value
 * @returns {Partial<PresetV1>}
 */
export function setGlobalEnvControlBinding(preset, controlKey, bindingName) {
  if (!GLOBAL_ENV_KEYS.has(controlKey)) return { ...preset };
  const allowed = GLOBAL_ENV_BINDING_TARGETS.some((t) => t.key === controlKey);
  if (!allowed) return { ...preset };
  const nextBindingName = String(bindingName || '').trim();
  return {
    ...preset,
    bindings: { ...(preset?.bindings || {}), [controlKey]: nextBindingName },
  };
}

export function setUnifiedControlValue(sceneMeta, preset, controlKey, value) {
  const control = deriveSceneControlDefsForSceneTab(sceneMeta, preset).find((item) => item.key === controlKey);
  if (!control) return { ...preset };

  if (control.legacy?.paramKey) {
    const nextParams = {
      ...(preset?.params || {}),
      [control.legacy.paramKey]: value,
    };
    return { ...preset, params: nextParams };
  }

  if (control.origin === 'discovered') {
    return {
      ...preset,
      discoveredControls: updateFutureControlCollection(preset?.discoveredControls, controlKey, {
        value,
        defaultValue: value,
      }),
      importedControls: updateFutureControlCollection(preset?.importedControls, controlKey, {
        value,
        defaultValue: value,
      }),
    };
  }

  return { ...preset };
}

/**
 * Apply a unified binding edit and return a legacy-compatible preset.
 * Future discovered/imported controls keep their binding choice in the seed
 * arrays until they are backed by runtime translation.
 *
 * @param {{
 *   defaultBindings?: Record<string, string>,
 *   defaultParams?: Record<string, unknown>,
 *   paramSpecs?: Record<string, any>,
 *   bindingTargets?: Array<{ key: string, label?: string, min?: number, max?: number }>,
 * }} sceneMeta
 * @param {Partial<PresetV1>} preset
 * @param {string} controlKey
 * @param {string} bindingName
 * @returns {Partial<PresetV1>}
 */
export function setUnifiedControlBinding(sceneMeta, preset, controlKey, bindingName) {
  const control = deriveSceneControlDefsForSceneTab(sceneMeta, preset).find((item) => item.key === controlKey);
  if (!control) return { ...preset };

  const nextBindingName = String(bindingName || '').trim();
  if (control.legacy?.bindingKey) {
    const nextBindings = {
      ...(preset?.bindings || {}),
      [control.legacy.bindingKey]: nextBindingName,
    };
    return { ...preset, bindings: nextBindings };
  }

  if (control.origin === 'discovered') {
    return {
      ...preset,
      discoveredControls: updateFutureControlCollection(preset?.discoveredControls, controlKey, {
        bindingDefault: nextBindingName,
      }),
      importedControls: updateFutureControlCollection(preset?.importedControls, controlKey, {
        bindingDefault: nextBindingName,
      }),
    };
  }

  return { ...preset };
}
